import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import { compact, get } from 'lodash-es';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { TrackingHandler } from 'client/tracking/handler';
import { EngineTypeQuizMap } from 'client/tracking/maps/common/engine-type-quiz';
import { HomepageAdExtensionMap } from 'client/tracking/maps/common/homepage-ad-extension';
import { TrimPricingBarsMap } from 'client/tracking/maps/common/trim-pricing-bars';
import { EditorialMap } from 'client/tracking/maps/editorial';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities, CmsModel, CmsPaths } from 'client/data/models/cms';
import {
  buildVehicleDataPath,
  VehicleEditorialEntities,
  VehicleEditorialModel,
} from 'client/data/models/vehicle-editorial';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { DEFAULT_CONTENT, extractMetadata } from 'client/data/cms/content';
import { HERO_HEIGHT, HERO_SIZE } from 'site-modules/shared/constants/editorial/editorial';
import { EDITORIAL_HEADER_SRC_INLINE } from 'site-modules/shared/constants/editorial/editorial-header';
import {
  AD_UNIT_LIMIT,
  NUMBER_OF_ENTRIES_BETWEEN_AD_UNITS,
} from 'site-modules/shared/constants/editorial/research-article';
import { CAR_NEWS_RSS_FEED_URL } from 'site-modules/shared/constants/editorial/rss';
import { DEALERLESS_OEM } from 'site-modules/shared/constants/oem';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { scrollToHash } from 'client/utils/scroll';
import { EV_RANGE_LEADERBOARD_HOSTING_PAGE } from 'client/site-modules/editorial/constants/ev-range-charts';
import { isCarNews as isCarNewsCategory } from 'client/site-modules/editorial/utils/car-news';
import { getHeroPhoto } from 'client/site-modules/editorial/utils/hero-photo';
import { getBreadcrumbsFromConfig, getResearchArticleBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import {
  getResearchArticleIndexPath,
  getTypeArticleConfigPath,
  getTypeArticleContentPath,
} from 'site-modules/editorial/utils/content-path';
import { normalizeContentVehicle } from 'site-modules/editorial/utils/content-vehicle';
import { getMoreInCategoriesLinks } from 'site-modules/editorial/utils/entry-converter';
import { isEvArticle } from 'site-modules/editorial/utils/ev';
import { transformAllAuthorsContent } from 'site-modules/shared/utils/editorial/author';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { isPreProd } from 'site-modules/shared/utils/publication-states';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { TOP_RATED_2026_CAR_NEWS_BLACKLISTED_ARTICLE_IDS } from 'site-modules/editorial/pages/research-article/research-article-definition';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { TrimPricingBars } from 'site-modules/shared/components/core-page/trim-pricing-bars/trim-pricing-bars';
import { ArticleMenu } from 'site-modules/shared/components/editorial/article-menu/article-menu';
import { ArticleMetadata } from 'site-modules/shared/components/editorial/article-metadata/article-metadata';
import { ArticlePhoto } from 'site-modules/shared/components/editorial/article-photo/article-photo';
import { ArticleTable } from 'site-modules/shared/components/editorial/article-table/article-table';
import { AllAuthorsMetadata } from 'site-modules/shared/components/editorial/author-metadata/all-authors-metadata';
import { EvRangeTable } from 'site-modules/shared/components/editorial/ev-range-table/ev-range-table';
import { EngineTypeQuiz } from 'site-modules/shared/components/engine-type-quiz/engine-type-quiz';
import { ExcoVideoViewerBlueBg } from 'site-modules/shared/components/exco-video/exco-video-viewer-blue-bg';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { EmbeddableInfographic } from 'site-modules/shared/components/infographics/embeddable-infographic/embeddable-infographic';
import { ArticleOffersAd } from 'site-modules/shared/components/native-ad/article-module-ad/article-offers-ad';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NativeVideoAd } from 'site-modules/shared/components/native-ad/video/video-native-ad';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { RightRailStickyInterscroll } from 'site-modules/shared/components/right-rail-sticky-interscroll/right-rail-sticky-interscroll';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { Share } from 'site-modules/shared/components/share/share';
import { CarNewsMarquee } from 'site-modules/shared/components/upper-funnel/home-marquee/car-news-marquee';
import { EmbeddedYoutubeAsync } from 'site-modules/shared/components/video/embedded-youtube/embedded-youtube-async';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import { ArticleContent } from 'site-modules/editorial/components/article-content/article-content';
import { ArticleEvToolLink } from 'site-modules/editorial/components/article-ev-tool-link/article-ev-tool-link';
import { ArticleReadyForEv } from 'site-modules/editorial/components/article-ready-for-ev/article-ready-for-ev';
import { ArticleSrpLink } from 'site-modules/editorial/components/article-srp-link/article-srp-link';
import { ArticleTreehousePromotion } from 'site-modules/editorial/components/article-treehouse-promotion/article-treehouse-promotion';
import { CardsCarousel } from 'site-modules/editorial/components/car-news/cards-carousel/cards-carousel';
import { VideoCard } from 'site-modules/editorial/components/car-news/video-card/video-card';
import { DynamicLeaderboardTable } from 'site-modules/editorial/components/dynamic-leaderboard-table/dynamic-leaderboard-table';
import { DynamicLeaderboard } from 'site-modules/editorial/components/dynamic-leaderboard/dynamic-leaderboard';
import { EmailGate } from 'site-modules/editorial/components/email-gate/email-gate';
import { EvRangeLeaderBoard } from 'site-modules/editorial/components/ev-range-leaderboard/ev-range-leaderboard';
import { FastChargingLeaderboardUI } from 'site-modules/editorial/components/fast-charging-leaderboard/fast-charging-leaderboard';
import { GooglePreferredSource } from 'site-modules/editorial/components/google-preferred-source/google-preferred-source';
import { InlineArticle } from 'site-modules/editorial/components/inline-article/inline-article';
import { InlineVideoSponsor } from 'site-modules/editorial/components/inline-video-sponsor/inline-video-sponsor';
import { NewsSubscriptionButton } from 'site-modules/editorial/components/news-subscription-button/news-subscription-button';
import { SocialEmbed } from 'site-modules/editorial/components/social-embed/social-embed';
import { SponsoredArticleHeader } from 'site-modules/editorial/components/sponsored-article-header/sponsored-article-header';
import { CompareModule } from 'site-modules/editorial/components/type/compare-module/compare-module';
import { UdragsLeaderboardUI } from 'site-modules/editorial/components/udrags-leaderboard/udrags-leaderboard';

import './article.scss';

const MORE_LINKS_NUMBER = 3;
const canonicalOrigin = 'https://www.edmunds.com';

const MMRECT_OPTIONS = {
  adName: 'mmrect',
  xs: true,
  sm: true,
  md: true,
  verticalOffset: '100%',
  useMinHeight: true,
  dfpCollapse: true,
  customSizes: [
    [1, 1],
    [320, 50],
    [300, 90],
    [300, 180],
    [300, 250],
  ],
};

function TypeArticleUI({
  content = DEFAULT_CONTENT,
  allAuthors = {
    author: DEFAULT_CONTENT,
    coAuthor: DEFAULT_CONTENT,
    editor: DEFAULT_CONTENT,
  },
  config = DEFAULT_CONTENT,
  slug,
  articleId = '',
  contentVehicle = null,
  visitorLocation = null,
  pathname = '',
  isMobile = false,
}) {
  const isCarNewsArticle = isCarNewsCategory(slug);
  const [videoModalData, setVideoModalData] = useState({});
  const [isVideoModalOpen, toggleVideoModalOpen] = useToggle(false);
  useEffect(() => {
    TrackingHandler.useMap({
      ...EditorialMap,
      ...EngineTypeQuizMap,
      ...HomepageAdExtensionMap,
      ...(isCarNewsArticle && TrimPricingBarsMap),
    });
    scrollToHash(window.location.hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const suppressAds = TOP_RATED_2026_CAR_NEWS_BLACKLISTED_ARTICLE_IDS.includes(articleId);

  const onToggleVideoModal = useCallback(newVideoModalData => {
    toggleVideoModalOpen();
    setVideoModalData(newVideoModalData || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let configContent = config;
  let hasVideo = false;
  let hasVideoModal = false;
  let hasExcoVideo = false;

  if (configContent.children && configContent.children().length > 0) {
    configContent = configContent.children()[0];
  }
  const isInternational = isUnknownZip(visitorLocation);
  const contentVehicleParams = {
    make: contentVehicle?.make?.slug,
    model: contentVehicle?.model?.slug,
    year: contentVehicle?.year,
  };
  const canonicalURL = getCanonical(content);
  const suppressMarquee = content.metadata('suppressMarquee').boolean();
  const pageHref = canonicalOrigin + pathname;
  const moreInCategories = configContent.child(`right-rail-links-${slug}`).children();
  const articleMenuLinks = getMoreInCategoriesLinks(canonicalURL, moreInCategories, false, MORE_LINKS_NUMBER);
  const inlineContent = configContent.child(`inline-content-${slug}`);
  const inlineContentPositions = inlineContent
    .metadata('position')
    .value()
    .split(',')
    .map(position => parseInt(position, 10));
  const breadcrumbsData = isCarNewsArticle
    ? getResearchArticleBreadcrumbs([slug], content.title, pathname)
    : getBreadcrumbsFromConfig(config, pathname, content.title);
  const sponsoredContent = content.child('sponsored-content');
  const isSponsoredContent = sponsoredContent.metadata('sponsored-title').value() === 'true';
  const { title, subtitle } = content;
  const heroPhoto = getHeroPhoto(content);
  const isSuvBuyingGuideArticle = pathname.includes('suv-buying-guide');
  const isEvLeaderBoardPage = pathname === EV_RANGE_LEADERBOARD_HOSTING_PAGE;
  const isEvArticlePage = isEvArticle({ slug, categories: content.categories });

  const contentEntries = [];
  let entriesToRenderCount = 0;
  let mmrectPos = 1;

  const renderExcoVideo = () => {
    // eslint-disable-next-line react-hooks/immutability
    hasExcoVideo = true;
    return <ExcoVideoViewerBlueBg />;
  };

  content.children().forEach(entry => {
    contentEntries.push(entry);
    entriesToRenderCount += 1;

    // Ad Units
    const suitableAdPosition = entriesToRenderCount % NUMBER_OF_ENTRIES_BETWEEN_AD_UNITS === 0;
    if (suitableAdPosition && mmrectPos <= AD_UNIT_LIMIT) {
      contentEntries.push({
        id: 'ad-unit',
        options: { ...MMRECT_OPTIONS, position: `${mmrectPos}` },
      });
      mmrectPos += 1;
    }
  });
  if (isSuvBuyingGuideArticle) {
    contentEntries.splice(contentEntries.length - 1, 0, {
      id: 'ad-unit',
      options: { ...MMRECT_OPTIONS, position: `${mmrectPos}` },
    });
  }

  const contentConfig = contentEntries.reduce((entries, entry, index) => {
    switch (entry.id) {
      case 'html': {
        const htmlContent = entry.metadata('htmlContent').value();
        entries.push({
          content: <ArticleContent content={htmlContent} index={index} />,
          key: `article-${entry.id}-${index}`,
        });

        break;
      }
      case 'table': {
        if (isEvLeaderBoardPage) {
          entries.push({
            content: (
              <EvRangeTable
                shareOptions={{
                  canonical: canonicalURL,
                  pageTitle: title,
                }}
              />
            ),
            key: `article-${entry.id}-${index}`,
          });

          break;
        }

        entries.push({
          content: (
            <ArticleTable
              className="mb-2_5"
              entry={entry}
              shareOptions={{
                contentId: content.id,
                canonical: canonicalURL,
                entryPath: [entry.id],
                pageTitle: title,
              }}
            />
          ),
          key: `article-${entry.id}-${index}`,
        });
        break;
      }
      case 'photo': {
        const photos = entry.links();
        entries.push({
          content: <ArticlePhoto photos={photos} isCore7220Test />,
          key: `article-${entry.id}-${index}`,
        });
        break;
      }
      case 'video': {
        const videoId = entry.metadata('youtube-videoid').value();
        const isVertical = entry.metadata('vertical').boolean();
        const videoFloat = entry.metadata('float').value();
        const videoCreativeId = `${isVertical ? 'vertical' : 'horizontal'}-article-video-${index}`;

        if (videoId) {
          // YT video trumps over EXCO, reason: ad Ops targeting
          const videoComponent = (
            <EmbeddedYoutubeAsync
              videoId={videoId}
              videoTitle="inline article video"
              creativeId={videoCreativeId}
              videoAspectRatio={isVertical ? '9:16' : '16:9'}
              containerClassName={classnames({ 'rounded-8': isVertical })}
              className={classnames({ 'w-100 h-100': isVertical })}
              placeholderClassName={classnames({ 'h-100': isVertical })}
            />
          );

          entries.push({
            content: hasVideo ? videoComponent : <ScrollElement id="videos">{videoComponent}</ScrollElement>,
            key: `article-${entry.id}-${index}`,
            hashAnchorId: `vid_${videoId}`,
            creativeId: videoCreativeId,
            className: isVertical
              ? classnames('vertical-video w-100', {
                  [videoFloat === 'left' ? 'float-start me-2' : 'float-end ms-2']: !isMobile,
                  'mx-auto': isMobile,
                })
              : null,
          });
          hasVideo = true;
        }
        break;
      }
      case 'videos': {
        const { title: videosTitle } = entry;
        hasVideoModal = true;

        entries.push({
          content: (
            <Fragment>
              {!!videosTitle && <h2 className="heading-3 mb-1_5">{videosTitle}</h2>}
              <CardsCarousel
                content={entry}
                CardComponent={VideoCard}
                showArrows={!isMobile}
                centerMode={isMobile}
                openVideoModal={onToggleVideoModal}
                isMobile={isMobile}
              />
            </Fragment>
          ),
          key: `article-${entry.id}-${index}`,
        });

        break;
      }
      case 'compare': {
        entries.push({
          content: <CompareModule entry={entry} />,
          key: `compare-${entry.id}-${index}`,
        });
        break;
      }
      case 'leaderboard': {
        entries.push({
          content: <DynamicLeaderboard entry={entry} pathname={pathname} isMobile={isMobile} />,
          key: `leaderboard-${index}`,
        });
        break;
      }
      case 'leaderboard-table': {
        entries.push({
          content: <DynamicLeaderboardTable entry={entry} isMobile={isMobile} />,
          key: `leaderboard-table-${index}`,
        });
        break;
      }
      case 'info-graphic': {
        if (entry.child('vehicles').metadata('type').value() === 'u-drags') {
          entries.push({
            content: <UdragsLeaderboardUI content={entry} pathname={pathname} />,
            key: `info-graphic-${index}`,
          });

          break;
        }

        if (entry.child('vehicles').metadata('type').value() === 'fast-charging') {
          entries.push({
            content: <FastChargingLeaderboardUI content={entry} pathname={pathname} isMobile={isMobile} />,
            key: `info-graphic-${index}`,
          });

          break;
        }

        if (isEvLeaderBoardPage) {
          entries.push({
            content: <EvRangeLeaderBoard />,
            key: `info-graphic-${index}`,
          });

          break;
        }

        entries.push({
          content: (
            <EmbeddableInfographic
              imgSrc={entry.metadata('media-image').value()}
              imgAlt={entry.metadata('imageAltText').value()}
              imgUrl={entry.metadata('imageURL').value()}
              clickableUrl={entry.metadata('clickableURL').value()}
              btnText={entry.metadata('buttonTitle').value()}
              hideShareButton={entry.metadata('hideShareButton').value() === 'true'}
            />
          ),
          key: `info-graphic-${index}`,
        });
        break;
      }
      case 'social-embed': {
        entries.push({
          key: `social-embed-${index}`,
          content: <SocialEmbed content={entry.metadata('htmlContent').value()} />,
        });
        break;
      }
      case 'editorial-high-impact': {
        if (entry.hasMetadata('enabled') && !suppressAds) {
          entries.push({
            content: (
              <div className="clearfix">
                <div className="high-impact mx-auto">
                  <NativeAd
                    adNameWired="edhighimpact"
                    adNameMobile="medhighimpact"
                    nativeStyle="none"
                    position={'1'}
                    refreshable={false}
                    all
                  />
                </div>
              </div>
            ),
            key: 'edhighimpact-ad-block',
          });
        }
        break;
      }
      case 'inline-video-ad': {
        if (suppressAds) {
          break;
        }
        // Inline Video Ad
        const inlineVideoAd = entry;
        const context = {
          sponsorTitle: inlineVideoAd.metadata('sponsorTitle').value(),
        };
        context.type = 'Youtube';
        context.videoId = inlineVideoAd.metadata('youtube-videoid').value();
        context.playlistId = '';

        // Feed the object to the component
        entries.push({
          content: <InlineVideoSponsor {...context} />,
          key: `article-${entry.id}-video-ad-${index}`,
        });
        break;
      }
      case 'ad-unit': {
        if (suppressAds) {
          break;
        }
        const { adName, position, xs, sm, md, lg, xl, customSizes, useMinHeight } = entry.options;
        entries.push({
          content: (
            <Col
              xs={12}
              className={classnames(`ad-unit-${adName} hidden-lg-up mt-3_5`, {
                'mb-2': !isMobile,
              })}
            >
              <NativeAd
                adNameWired={adName}
                adNameMobile={adName}
                nativeStyle="none"
                position={position}
                wiredBreakpoints={{
                  xl: !!xl,
                  lg: !!lg,
                  md: !!md,
                }}
                mobileBreakpoints={{
                  xs: !!xs,
                  sm: !!sm,
                  md: !!md,
                }}
                useMinHeight={useMinHeight}
                refreshable={false}
                showLabel
                customSizes={customSizes}
              />
            </Col>
          ),
          key: `ad-unit-${index}`,
        });
        break;
      }
      case 'video-ad-unit': {
        if (suppressAds) {
          break;
        }
        entries.push({
          content: (
            <Col
              xs={12}
              className={classnames({
                'mt-1': isMobile,
                'd-none': !isMobile,
              })}
            >
              {isMobile && !hasVideo ? null : <NativeVideoAd mobileOnly useMinHeight />}
            </Col>
          ),
          key: `native-video-ad-${index}`,
        });
        break;
      }
      case 'article-offers-ad': {
        entries.push({
          content: <ArticleOffersAd entry={entry} isMobile={isMobile} />,
          key: `article-offers-ad-${index}`,
        });
        break;
      }
      default:
        break;
    }
    const positionIndex = inlineContentPositions.indexOf(index);
    if (positionIndex >= 0) {
      const inlineContentChild = inlineContent.child(`inline-content-${positionIndex + 1}`);
      if (inlineContentChild.id) {
        // If child not empty, id should exist
        const metadata = extractMetadata(['heading', 'image', 'url', 'title', 'text', 'ctaText'], inlineContentChild);

        entries.push({
          content: <InlineArticle content={metadata} />,
          key: `article-${inlineContentChild.id}-${index}-inline`,
        });
      }
    }
    return entries;
  }, []);

  const isDealerlessOem = DEALERLESS_OEM.includes(contentVehicle?.make?.slug);
  const hasSrpLink = isCarNewsArticle && !!contentVehicle?.model && !isDealerlessOem;

  const hasRightRailLinks = !!configContent.child(`right-rail-links-${slug}`).metadata('moduleHeader').value();

  const renderWiredVideoAd = hasVideo && !suppressAds && (
    <NativeVideoAd
      wrapperClass="native-video-wrapper mb-3_5"
      wiredOnly
      wiredBreakpoints={{
        lg: true,
        xl: true,
      }}
      useMinHeight
    />
  );

  const carNewsMarquee = !suppressAds && (
    <CarNewsMarquee className="mt-3_5 mb-3_5" scrollBtnPosition={isMobile ? 'start' : 'end'} />
  );

  const renderHero = (
    <AspectRatioContainer className="rounded-12 mb-1_5">
      <img src={getFullImageUrl(heroPhoto.href, HERO_SIZE[isMobile])} alt={heroPhoto.title || ''} />
    </AspectRatioContainer>
  );

  return (
    <Fragment>
      {isCarNewsArticle && <EmailGate />}
      <main id="main-content" className="editorial-type-article pos-r mb-3_5" data-ad-zone={articleId}>
        <img
          src={EDITORIAL_HEADER_SRC_INLINE[isMobile]}
          alt=""
          fetchPriority="high"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: HERO_HEIGHT[isMobile],
          }}
        />
        <Container
          className="text-shadow-contrast-high"
          style={heroPhoto ? undefined : { height: HERO_HEIGHT[isMobile] }}
        >
          <Breadcrumbs className="ps-0 pt-0_5 pb-0 text-white mb-0_5 mb-md-1" data={breadcrumbsData} />
          <Row>
            <Col xs={12} lg={9}>
              <h1
                className={classnames('text-white fw-bold', {
                  'mb-0-75': !!subtitle,
                  'size-36': !isMobile,
                  'size-24': isMobile,
                })}
              >
                {title}
              </h1>
              {!!subtitle && <h2 className="size-16 text-white fw-medium mb-2">{subtitle}</h2>}
            </Col>
          </Row>
        </Container>
        <Container>
          {isSponsoredContent && (
            <SponsoredArticleHeader sponsoredContent={sponsoredContent} title={title} canonicalURL={canonicalURL} />
          )}
          <Row className="mb-2">
            <Col xs={12} lg={8} data-sticky-bottom-boundary>
              {!!heroPhoto && renderHero}
              {!isSponsoredContent && (
                <ArticleMetadata
                  className="mb-2"
                  content={content}
                  allAuthors={allAuthors}
                  creativeId="edm-entry-article-metadata"
                  rssLink={isCarNewsArticle ? CAR_NEWS_RSS_FEED_URL : undefined}
                  isMobile={isMobile}
                />
              )}
              {contentConfig.map((item, index) => {
                const contentAfter = compact([
                  index === 1 && (
                    <Fragment>
                      {hasSrpLink && (
                        <ArticleSrpLink
                          className="mb-2 px-1_5 px-md-0"
                          contentVehicle={contentVehicle}
                          isMobile={isMobile}
                        />
                      )}
                      {!hasVideo && !hasExcoVideo && renderExcoVideo()}
                      <hr className="mb-1_5 mt-0" />
                    </Fragment>
                  ),
                  index === 1 && !hasSrpLink && (
                    <ArticleEvToolLink
                      categories={content.categories}
                      slug={slug}
                      className="mb-3_5 px-1_5 px-md-0"
                      isMobile={isMobile}
                    />
                  ),
                  index === 1 && (
                    <ArticleTreehousePromotion
                      className="mb-3_5"
                      categories={content.categories}
                      slug={slug}
                      fallback={<ArticleReadyForEv categories={content.categories} slug={slug} className="mb-3_5" />}
                    />
                  ),
                  index === 4 &&
                    isCarNewsArticle &&
                    !!contentVehicle?.year &&
                    !isPreProd(contentVehicle?.pubStates) && (
                      <TrimPricingBars
                        className="mb-2"
                        params={contentVehicleParams}
                        vehicle={contentVehicle}
                        isInternational={isInternational}
                        hidePricingAd
                      />
                    ),
                ]);

                return (
                  <Fragment key={item.key}>
                    <div
                      className={classnames('research-article-section', item.className, {
                        'mb-2': !(index === 1 && hasSrpLink) && !item.key.includes('ad-unit'),
                        clearfix: contentAfter?.length,
                      })}
                      data-tracking-parent={item.creativeId || `edm-entry-research-article-section-${index}`}
                      name={item.hashAnchorId || null}
                    >
                      {item.content}
                    </div>
                    {contentAfter.map((afterEntry, afterIndex) => {
                      const afterKey = `section-${index}-${afterIndex}`;
                      return <Fragment key={afterKey}>{afterEntry}</Fragment>;
                    })}
                  </Fragment>
                );
              })}
              {isCarNewsArticle && !suppressMarquee && carNewsMarquee}
              <div className="mt-3_5 text-center text-md-start">
                <Share url={pageHref} title={content.title} />
              </div>
              {isCarNewsArticle && (
                <Experiment name="core-7788-preferred" showDefault>
                  <Recipe name="ctrl" isDefault />
                  <Recipe name="chal">
                    <GooglePreferredSource className="mb-2" />
                  </Recipe>
                </Experiment>
              )}
              {!isSponsoredContent && (
                <AllAuthorsMetadata
                  allAuthors={allAuthors}
                  authorNameNoLink={content.metadata('authorNameNoLink').value()}
                />
              )}
              {isCarNewsArticle && (
                <NewsSubscriptionButton className="mt-3_5 mb-2 px-1_5 px-md-0" isMobile={isMobile} />
              )}
            </Col>
            <Col className="hidden-lg-down py-3_5 my-3_5" lg={4}>
              <RightRailStickyInterscroll>
                {!suppressAds && (
                  <MrectNative
                    wrapperClass="hidden-lg-down mb-3_5"
                    position={'1'}
                    refreshable={false}
                    wiredBreakpoints={{ lg: true, xl: true }}
                    wiredOnly
                    useMinHeight
                  />
                )}
                {hasRightRailLinks && (
                  <ArticleMenu
                    className="mb-1_5"
                    title={configContent.child(`right-rail-links-${slug}`).metadata('moduleHeader').value()}
                    links={articleMenuLinks}
                    data-tracking-parent="edm-entry-right-rail-menu"
                  />
                )}
                {!suppressAds && (
                  <MrectNative
                    wrapperClass="hidden-lg-down mb-3_5"
                    position={'2'}
                    refreshable={false}
                    wiredBreakpoints={{ lg: true, xl: true }}
                    wiredOnly
                    useMinHeight
                  />
                )}
                {renderWiredVideoAd}
                {slug === 'truck' && !isMobile && !suppressAds && (
                  <NativeAd
                    adNameWired="mrect"
                    nativeStyle="none"
                    wrapperClass="hidden-lg-down"
                    position={'3'}
                    refreshable={false}
                    wiredBreakpoints={{ lg: true, xl: true }}
                    useMinHeight
                    showLabel
                    addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
                  />
                )}
                {slug !== 'truck' && !isMobile && !suppressAds && (
                  <MrectNative
                    wrapperClass="hidden-lg-down"
                    position={'3'}
                    refreshable={false}
                    wiredBreakpoints={{ lg: true, xl: true }}
                    wiredOnly
                    useMinHeight
                    addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
                  />
                )}
              </RightRailStickyInterscroll>
            </Col>
          </Row>
        </Container>
        {isEvArticlePage && (
          <Container className="mb-2">
            <Row>
              <Col xs={12} lg={8}>
                <EngineTypeQuiz />
              </Col>
            </Row>
          </Container>
        )}
      </main>
      {hasVideoModal && (
        <YoutubeVideoModalAsync
          onToggleModal={onToggleVideoModal}
          isOpen={isVideoModalOpen}
          videoId={videoModalData.videoId}
          videoAspectRatio={videoModalData.videoAspectRatio}
          creativeId={videoModalData.creativeId}
          videoTitle={videoModalData.videoTitle}
        />
      )}
      <PageLinksWidget className="container mb-3" />
    </Fragment>
  );
}

TypeArticleUI.propTypes = {
  content: CmsEntities.Content,
  config: CmsEntities.Content,
  allAuthors: PropTypes.shape({
    author: CmsEntities.Content,
    coAuthor: CmsEntities.Content,
    editor: CmsEntities.Content,
  }),
  contentVehicle: VehicleEditorialEntities.vehicle,
  visitorLocation: VisitorEntities.Location,
  slug: PropTypes.string.isRequired,
  splat: PropTypes.string,
  articleId: PropTypes.string,
  pathname: PropTypes.string,
  isMobile: PropTypes.bool,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  pathname: get(state, 'pageContext.location.pathname'),
  breakpoint: state.pageContext.breakpointDeprecated2,
});

export const TypeArticle = connect(mapStateToProps)(
  connectToModel(TypeArticleUI, {
    allAuthors: bindToPath(
      ({ slug, articleId, splat }) =>
        CmsPaths.buildAllAuthorsPath({
          contentPath: isCarNewsCategory(slug)
            ? getResearchArticleIndexPath(slug, articleId, splat)
            : getTypeArticleContentPath(slug, articleId),
        }),
      CmsModel,
      transformAllAuthorsContent
    ),
    config: bindToContent(
      ({ slug, articleId }) => !isCarNewsCategory(slug) && getTypeArticleConfigPath(slug, articleId)
    ),
    contentVehicle: bindToPath(
      ({ content }) => buildVehicleDataPath(normalizeContentVehicle(content?.vehicles?.[0])),
      VehicleEditorialModel
    ),
    visitorLocation: bindToPath('location', VisitorModel),
  })
);
